import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";
import GridView from "components/GridView";
import useGridStore from "components/GridView/grid.store";
import { FinancialStatementService } from "services/financialStatement";
import { useNotification } from "hooks/useNotification";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import IconButton from "components/blocks/IconButton";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import { FinancialStatementLocalResult } from "services/financialStatement/Models/FinancialStatementTypes";
import { useCookieContext } from "contexts";
import { prepareFilters } from "components/GridView/components/GridFilter";
import { useTitle } from "hooks/useTitle";
import { Modal } from "react-bootstrap";
import CreateFinancialStatementModal from "./components/CreateFinancialStatementModal";
import ViewFinancialStatementModal from "./components/ViewFinancialStatementModal";

export default function FinancialStatementGridView() {
  const { t } = useTranslation(["FinancialStatement", "Common"]);
  useTitle(t("financialStatements"));
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { showNotification } = useNotification();
  const { setBreadcrumbs } = useBreadcrumb();
  const { isAr } = useCookieContext();

  const [loading, setLoading] = useState(false);
  const [financialStatements, setFinancialStatements] = useState<FinancialStatementLocalResult[] | undefined>();
  const [totalCount, setTotalCount] = useState(0);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<number | null>(null);
  const [deleteConfirmModalVisible, setDeleteConfirmModalVisible] = useState(false);
  const [setPaidConfirmModalVisible, setSetPaidConfirmModalVisible] = useState(false);
  const [actionStatementId, setActionStatementId] = useState<number | null>(null);
  const ready = useRef(false);

  const { sortState, pagination, setPagination, searchValue, filters, resetGridStore } = useGridStore(
    useShallow((state) => ({
      sortState: state.sortState,
      pagination: state.pagination,
      setPagination: state.setPagination,
      searchValue: state.generalSearch,
      filters: state.filters,
      resetGridStore: state.reset,
    })),
  );

  const fetchFinancialStatements = async ({ resetPagination = false }: { resetPagination?: boolean } = {}) => {
    try {
      setLoading(true);
      const response = await FinancialStatementService.getAllFinancialStatements({
        pageIndex: resetPagination ? 1 : pagination.currentPage,
        pageSize: 10,
        search: searchValue,
        sort: sortState
          ? Object.keys(sortState).map((key) => ({
              field: key,
              dir: sortState[key],
            }))
          : [],
        filter: prepareFilters(filters),
      });

      setFinancialStatements(response.data.result);
      setTotalCount(response.data.totalCount);

      if (resetPagination) {
        setPagination({
          currentPage: 1,
          totalPages: Math.ceil(response.data.totalCount / 10),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
      setPagination({
        currentPage: 1,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchFinancialStatements();
  }, [pagination.currentPage]);

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchFinancialStatements({ resetPagination: true });
  }, [sortState, searchValue, filters, isAr]);

  useEffect(() => {
    if (!ready.current) {
      resetGridStore();
      ready.current = true;
      return;
    }
  }, [resetGridStore]);

  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: "financialStatements",
        path: "/home/financialStatements",
        menuItemId: "financialStatements",
      },
    ]);
  }, []);

  const handleDelete = async () => {
    if (!actionStatementId) return;

    try {
      const response = await FinancialStatementService.deleteFinancialStatement(actionStatementId);
      if (!response.hasError) {
        showNotification({
          type: "success",
          description: t("financialStatementDeletedSuccessfully"),
        });
        fetchFinancialStatements({ resetPagination: true });
      } else {
        showNotification({
          type: "error",
          description: response.description || t("Common:internalServerError"),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setDeleteConfirmModalVisible(false);
      setActionStatementId(null);
    }
  };

  const handleSetAsPaid = async () => {
    if (!actionStatementId) return;

    try {
      const response = await FinancialStatementService.setAsPaid(actionStatementId);
      if (!response.hasError) {
        showNotification({
          type: "success",
          description: t("financialStatementMarkedAsPaid"),
        });
        fetchFinancialStatements({ resetPagination: true });
      } else {
        showNotification({
          type: "error",
          description: response.description || t("Common:internalServerError"),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setSetPaidConfirmModalVisible(false);
      setActionStatementId(null);
    }
  };

  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "serialNumber",
        displayName: t("serialNumber"),
        showOnMobile: true,
        sortable: true,
        render: (row: FinancialStatementLocalResult) => {
          return row.serialNumber;
        },
      },
      {
        field: "totalAmount",
        displayName: t("totalAmount"),
        sortable: true,
        filterable: true,
        render(row: FinancialStatementLocalResult) {
          return (
            <span>
              {row.totalAmount.toFixed(2)} {t("riyal")}
            </span>
          );
        },
      },
      {
        field: "researchPaymentsCount",
        displayName: t("researchPaymentsCount"),
        sortable: true,
        render(row: FinancialStatementLocalResult) {
          return <span>{row.researchPaymentsCount}</span>;
        },
      },
      {
        field: "createdOn",
        displayName: t("createdOn"),
        sortable: true,
        filterable: true,
        filterOptions: {
          type: "date",
        },
        render(row: FinancialStatementLocalResult) {
          return <>{toSystemThemeDateFormat(row.createdOn)}</>;
        },
      },
      {
        field: "isPaid",
        displayName: t("isPaid"),
        sortable: true,
        filterable: true,
        showOnMobile: true,
        filterOptions: {
          type: "select",
          options: [
            { id: "true", value: t("paid") },
            { id: "false", value: t("unpaid") },
          ],
        },
        render(row: FinancialStatementLocalResult) {
          const statusClassName = row.isPaid
            ? "bg-success text-success"
            : "bg-warning text-warning";
          return (
            <span className={`badge rounded-4 ${statusClassName} bg-opacity-10 py-2`}>
              {row.isPaid ? t("paid") : t("unpaid")}
            </span>
          );
        },
      },
      {
        field: "actions",
        displayName: t("Common:actions"),
        showOnMobile: true,
        render: (row: FinancialStatementLocalResult) => {
          return (
            <div className="d-flex gap-2">
              <IconButton
                icon="icon-eye"
                fitToIconWidth
                innerIconColor="info"
                size="md"
                title={t("view")}
                onClick={() => {
                  setSelectedStatementId(row.id);
                  setViewModalVisible(true);
                }}
              />
              <IconButton
                icon="icon-download"
                fitToIconWidth
                innerIconColor="secondary"
                size="md"
                title={t("download")}
                onClick={async () => {
                  try {
                    await FinancialStatementService.downloadFinancialStatement(row.id);
                  } catch (error) {
                    showNotification({
                      type: "error",
                      description: t("Common:internalServerError"),
                    });
                  }
                }}
              />
              {!row.isPaid && (
                <>
                  <IconButton
                    icon="icon-delete"
                    fitToIconWidth
                    innerIconColor="danger"
                    size="md"
                    title={t("delete")}
                    onClick={() => {
                      setActionStatementId(row.id);
                      setDeleteConfirmModalVisible(true);
                    }}
                  />
                  <IconButton
                    icon="icon-check"
                    fitToIconWidth
                    innerIconColor="success"
                    size="md"
                    title={t("setAsPaid")}
                    onClick={() => {
                      setActionStatementId(row.id);
                      setSetPaidConfirmModalVisible(true);
                    }}
                  />
                </>
              )}
            </div>
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("allFinancialStatements"),
    singularName: t("financialStatement"),
    totalCount: totalCount,
    actionsComponent: (
      <button className="btn btn-primary px-4" onClick={() => setCreateModalVisible(true)}>
        <i className="icon-plus mx-2"></i>
        {t("createFinancialStatement")}
      </button>
    ),
  };

  return (
    <div>
      <GridView
        loading={loading}
        gridProps={{
          data: financialStatements ?? [],
          gridSchema,
        }}
        viewHeaderProps={viewHeader}
      />

      {createModalVisible && (
        <CreateFinancialStatementModal
          isOpen={createModalVisible}
          onClose={(refetch) => {
            setCreateModalVisible(false);
            if (refetch) {
              fetchFinancialStatements({ resetPagination: true });
            }
          }}
        />
      )}

      {viewModalVisible && selectedStatementId && (
        <ViewFinancialStatementModal
          isOpen={viewModalVisible}
          statementId={selectedStatementId}
          onClose={() => {
            setViewModalVisible(false);
            setSelectedStatementId(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={deleteConfirmModalVisible} onHide={() => setDeleteConfirmModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="w-100">{t("delete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirmDelete")}</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setDeleteConfirmModalVisible(false)}>
            <i className="icon-close mx-2"></i>
            {t("cancel")}
          </button>
          <button className="btn btn-danger" onClick={handleDelete}>
            <i className="icon-delete mx-2"></i>
            {t("delete")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Set As Paid Confirmation Modal */}
      <Modal show={setPaidConfirmModalVisible} onHide={() => setSetPaidConfirmModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="w-100">{t("setAsPaid")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirmSetAsPaid")}</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setSetPaidConfirmModalVisible(false)}>
            <i className="icon-close mx-2"></i>
            {t("cancel")}
          </button>
          <button className="btn btn-success" onClick={handleSetAsPaid}>
            <i className="icon-check mx-2"></i>
            {t("confirm")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
